import React, { useEffect, useState, useRef } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { Save, Upload, Eye, Award, ToggleLeft, ToggleRight, CheckCircle2, AlertTriangle } from "lucide-react";
import ColorField from "../../components/ColorField";
import AdminDiplomaTextSection from "../../components/admin/AdminDiplomaTextSection";

const backendUrl = process.env.REACT_APP_BACKEND_URL;

function UploadField({ label, path, onUploaded, testid }) {
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onUploaded(data.path || data.storage_path || data.url || "");
      toast.success(`${label} carregado`);
    } catch (err) { toast.error("Falha no upload"); }
    setBusy(false);
  };
  return (
    <div>
      <label className="block text-xs font-semibold text-[#0B1D3A]/70 mb-1">{label}</label>
      <div className="flex items-center gap-2">
        <label className="inline-flex items-center gap-1 text-xs px-3 py-2 rounded-lg bg-[#0B1D3A]/5 hover:bg-[#0B1D3A]/10 cursor-pointer" data-testid={testid}>
          <Upload size={13} /> {busy ? "A carregar..." : "Carregar"}
          <input type="file" accept="image/*" className="hidden" onChange={upload} />
        </label>
        {path ? <span className="text-xs text-[#2D9C5A] truncate max-w-[140px]" title={path}>✓ carregado</span> : <span className="text-xs text-[#0B1D3A]/40">nenhum</span>}
        {path ? <button type="button" onClick={() => onUploaded("")} className="text-xs text-red-600">remover</button> : null}
      </div>
    </div>
  );
}

function CategoryCard({ cfg, onChange, onSave }) {
  const f = (k) => cfg[k] ?? "";
  return (
    <div className="rounded-2xl bg-white p-5 border border-[#0B1D3A]/5 space-y-3" data-testid={`diploma-cat-${cfg.category}`}>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="font-outfit font-bold text-[#0B1D3A] flex items-center gap-2"><Award size={18} className="text-[#FF6B6B]" /> {cfg.label_pt}</h3>
        <div className="flex items-center gap-3">
          <button type="button" onClick={() => onChange("active", !cfg.active)} className="inline-flex items-center gap-1 text-sm font-semibold" data-testid={`diploma-cat-${cfg.category}-active`}>
            {cfg.active ? <ToggleRight size={22} className="text-[#2D9C5A]" /> : <ToggleLeft size={22} className="text-[#0B1D3A]/40" />}
            {cfg.active ? "Ativo" : "Inativo"}
          </button>
          <a href={`${backendUrl}/api/diploma/preview?lang=pt&category=${cfg.category}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg bg-[#FFD93D]/40 text-[#856404]" data-testid={`diploma-cat-${cfg.category}-preview`}><Eye size={13} /> Pré-visualizar</a>
        </div>
      </div>
      <p className="text-xs text-[#0B1D3A]/50">Critério automático: {cfg.category === "top10" ? "1.º–10.º lugar" : cfg.category === "top20" ? "11.º–20.º lugar" : cfg.category === "top50" ? "21.º–50.º lugar" : cfg.category === "above_avg" ? "Abaixo do Top 50, concluiu, tempo < média" : cfg.category === "below_avg" ? "Abaixo do Top 50, concluiu, tempo > média" : "Não concluiu (tempo esgotado)"}</p>

      <div className="grid grid-cols-2 gap-3">
        <UploadField label="Template (imagem de fundo)" path={cfg.template_path} onUploaded={(p) => onChange("template_path", p)} testid={`diploma-cat-${cfg.category}-tpl`} />
        <UploadField label="Logótipo" path={cfg.logo_path} onUploaded={(p) => onChange("logo_path", p)} testid={`diploma-cat-${cfg.category}-logo`} />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <label className="block text-xs">Título (PT)<input value={f("title_pt")} onChange={(e) => onChange("title_pt", e.target.value)} placeholder={cfg.label_pt} className="w-full mt-1 px-3 py-2 rounded-lg border border-[#0B1D3A]/15 text-sm" /></label>
        <label className="block text-xs">Título (EN)<input value={f("title_en")} onChange={(e) => onChange("title_en", e.target.value)} placeholder={cfg.label_en} className="w-full mt-1 px-3 py-2 rounded-lg border border-[#0B1D3A]/15 text-sm" /></label>
        <label className="block text-xs">Subtítulo (PT)<input value={f("subtitle_pt")} onChange={(e) => onChange("subtitle_pt", e.target.value)} placeholder={cfg.label_pt} className="w-full mt-1 px-3 py-2 rounded-lg border border-[#0B1D3A]/15 text-sm" /></label>
        <label className="block text-xs">Subtítulo (EN)<input value={f("subtitle_en")} onChange={(e) => onChange("subtitle_en", e.target.value)} placeholder={cfg.label_en} className="w-full mt-1 px-3 py-2 rounded-lg border border-[#0B1D3A]/15 text-sm" /></label>
        <label className="block text-xs">Mensagem (PT)<textarea value={f("message_pt")} onChange={(e) => onChange("message_pt", e.target.value)} rows={2} className="w-full mt-1 px-3 py-2 rounded-lg border border-[#0B1D3A]/15 text-sm" /></label>
        <label className="block text-xs">Mensagem (EN)<textarea value={f("message_en")} onChange={(e) => onChange("message_en", e.target.value)} rows={2} className="w-full mt-1 px-3 py-2 rounded-lg border border-[#0B1D3A]/15 text-sm" /></label>
        <label className="block text-xs">Rodapé (PT)<input value={f("footer_pt")} onChange={(e) => onChange("footer_pt", e.target.value)} className="w-full mt-1 px-3 py-2 rounded-lg border border-[#0B1D3A]/15 text-sm" /></label>
        <label className="block text-xs">Rodapé (EN)<input value={f("footer_en")} onChange={(e) => onChange("footer_en", e.target.value)} className="w-full mt-1 px-3 py-2 rounded-lg border border-[#0B1D3A]/15 text-sm" /></label>
      </div>
      <div className="flex items-center justify-between">
        <label className="flex items-center gap-2 text-xs">Cor do texto<ColorField value={cfg.text_color || "#0B1D3A"} onChange={(e) => onChange("text_color", e.target.value)} className="h-8 w-12 rounded border border-[#0B1D3A]/15" /></label>
        <button type="button" onClick={onSave} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#005F73] text-white font-semibold text-sm" data-testid={`diploma-cat-${cfg.category}-save`}><Save size={15} /> Guardar</button>
      </div>
    </div>
  );
}

export default function AdminDiplomas() {
  const [configs, setConfigs] = useState([]);
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const dirty = useRef(new Set());

  const load = async () => {
    setLoading(true);
    const [c, s] = await Promise.all([
      api.get("/admin/diploma-configs"),
      api.get("/settings"),
    ]);
    setConfigs(c.data || []);
    setSettings(s.data || {});
    dirty.current = new Set();
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const upload = async (file, field) => {
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      dirty.current.add(field);
      setSettings((f) => ({ ...f, [field]: data.path }));
      toast.success("Imagem carregada");
    } catch (e) { toast.error("Upload falhou"); }
  };

  const saveDiplomaText = async () => {
    try {
      const payload = {};
      dirty.current.forEach((k) => { payload[k] = settings[k]; });
      await api.put("/admin/settings", payload);
      dirty.current = new Set();
      toast.success("Textos e ranking guardados");
    } catch (e) { toast.error("Erro ao guardar"); }
  };

  const changeCfg = (idx, key, val) => {
    setConfigs((prev) => prev.map((c, i) => (i === idx ? { ...c, [key]: val } : c)));
  };
  const saveCfg = async (cfg) => {
    try {
      const { category, label_pt, label_en, ...payload } = cfg;
      await api.put(`/admin/diploma-configs/${category}`, payload);
      toast.success(`${label_pt} guardado`);
    } catch (e) { toast.error("Erro ao guardar"); }
  };

  const saveAvailability = async () => {
    try {
      await api.put("/admin/settings", {
        diplomas_available: !!settings.diplomas_available,
        diploma_unavailable_message_pt: settings.diploma_unavailable_message_pt || "",
        diploma_unavailable_message_en: settings.diploma_unavailable_message_en || "",
      });
      toast.success("Disponibilidade guardada");
    } catch (e) { toast.error("Erro"); }
  };

  if (loading) return <div className="text-[#0B1D3A]/50">A carregar...</div>;

  return (
    <div data-testid="admin-diplomas-page" className="space-y-6">
      <div>
        <h1 className="font-outfit text-3xl font-bold text-[#0B1D3A]">Gestão de Diplomas</h1>
        <p className="text-sm text-[#0B1D3A]/60 mt-1">Configura cada categoria de diploma, atribuída automaticamente pela classificação. As posições dos textos, o QR Code e o ranking público definem-se nesta página, mais abaixo.</p>
      </div>

      {/* Status banner */}
      {settings.diplomas_available ? (
        <div className="flex items-center gap-3 rounded-2xl border border-[#2D9C5A]/30 bg-[#2D9C5A]/10 px-5 py-4" data-testid="diploma-status-banner">
          <CheckCircle2 size={22} className="text-[#2D9C5A] shrink-0" />
          <div>
            <p className="font-outfit font-bold text-[#2D9C5A]">Diplomas: DISPONÍVEIS</p>
            <p className="text-xs text-[#0B1D3A]/60">Alunos e professores conseguem descarregar os diplomas neste momento.</p>
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-3 rounded-2xl border border-[#E63946]/30 bg-[#E63946]/10 px-5 py-4" data-testid="diploma-status-banner">
          <AlertTriangle size={22} className="text-[#E63946] shrink-0" />
          <div>
            <p className="font-outfit font-bold text-[#E63946]">Diplomas: INDISPONÍVEIS</p>
            <p className="text-xs text-[#0B1D3A]/60">Os utilizadores veem a mensagem definida abaixo em vez do diploma. (O admin mantém o acesso.)</p>
          </div>
        </div>
      )}

      {/* Availability */}
      <div className="rounded-2xl bg-white p-5 border border-[#0B1D3A]/5 space-y-3" data-testid="diploma-availability">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h2 className="font-outfit font-bold text-[#0B1D3A]">Disponibilidade dos diplomas</h2>
          <button type="button" onClick={() => setSettings((s) => ({ ...s, diplomas_available: !s.diplomas_available }))} className="inline-flex items-center gap-2 font-semibold" data-testid="diploma-available-toggle">
            {settings.diplomas_available ? <ToggleRight size={26} className="text-[#2D9C5A]" /> : <ToggleLeft size={26} className="text-[#0B1D3A]/40" />}
            {settings.diplomas_available ? "Disponíveis" : "Indisponíveis"}
          </button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <label className="block text-xs">Mensagem quando indisponível (PT)<input value={settings.diploma_unavailable_message_pt || ""} onChange={(e) => setSettings((s) => ({ ...s, diploma_unavailable_message_pt: e.target.value }))} className="w-full mt-1 px-3 py-2 rounded-lg border border-[#0B1D3A]/15 text-sm" data-testid="diploma-unavailable-msg-pt" /></label>
          <label className="block text-xs">Mensagem quando indisponível (EN)<input value={settings.diploma_unavailable_message_en || ""} onChange={(e) => setSettings((s) => ({ ...s, diploma_unavailable_message_en: e.target.value }))} className="w-full mt-1 px-3 py-2 rounded-lg border border-[#0B1D3A]/15 text-sm" data-testid="diploma-unavailable-msg-en" /></label>
        </div>
        <button onClick={saveAvailability} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#0B1D3A] text-white font-semibold text-sm" data-testid="diploma-availability-save"><Save size={15} /> Guardar disponibilidade</button>
      </div>

      {/* Categories */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        {configs.map((cfg, idx) => (
          <CategoryCard key={cfg.category} cfg={cfg} onChange={(k, v) => changeCfg(idx, k, v)} onSave={() => saveCfg(configs[idx])} />
        ))}
      </div>

      {/* Diploma texts/positions/QR + Ranking público (moved from "Outras definições") */}
      <AdminDiplomaTextSection form={settings} setForm={setSettings} dirty={dirty} upload={upload} backendUrl={backendUrl} />
      <button onClick={saveDiplomaText} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#0B1D3A] text-white font-bold" data-testid="diploma-text-save-btn">
        <Save size={16} /> Guardar textos e ranking
      </button>
    </div>
  );
}
